import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ActivityIndicator, RefreshControl } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import { listNotifications, getPosts, getDid } from '../services/bluesky';
import { extractPlainText } from '../utils/text';
import Hyperlink from '../components/Hyperlink';
import { Theme, useTheme, useThemedStyles } from '../theme';

const CACHE_KEY_NOTIFS = 'cache_notifications';
const CACHE_KEY_POST_CONTEXT = 'cache_post_context';

interface GroupedNotification {
  id: string;
  reason: string;
  subjectUri?: string;
  authors: any[];
  items: any[];
  indexedAt: string;
}

interface NotificationsProps {
  onSelectPost?: (post: any) => void;
  onSelectUser?: (did: string) => void;
  onSearchHashtag?: (hashtag: string) => void;
}

const Notifications: React.FC<NotificationsProps> = ({ onSelectPost, onSelectUser, onSearchHashtag }) => {
  const insets = useSafeAreaInsets();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [postContext, setPostContext] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const { theme } = useTheme();
  const styles = useThemedStyles(createStyles);
  const { t } = useTranslation();

  const getCacheKeys = useCallback(() => {
    const did = getDid();
    return {
      notifs: `${CACHE_KEY_NOTIFS}_${did}`,
      context: `${CACHE_KEY_POST_CONTEXT}_${did}`
    };
  }, []);

  const loadCache = useCallback(async () => {
    try {
      const did = getDid();
      if (!did) return;

      const keys = getCacheKeys();
      const cachedNotifs = await AsyncStorage.getItem(keys.notifs);
      const cachedContext = await AsyncStorage.getItem(keys.context);
      
      if (cachedNotifs) {
        setNotifications(JSON.parse(cachedNotifs));
        setLoading(false);
      }
      if (cachedContext) {
        setPostContext(JSON.parse(cachedContext));
      }
    } catch (e) {
      console.error('Failed to load notification cache', e);
    }
  }, [getCacheKeys]);

  const fetchPostContext = async (uris: string[]) => {
    if (uris.length === 0) return;
    try {
      const BATCH_SIZE = 25;
      const batches: string[][] = [];
      for (let i = 0; i < uris.length; i += BATCH_SIZE) {
        batches.push(uris.slice(i, i + BATCH_SIZE));
      }

      const allPosts: any[] = [];
      for (const batch of batches) {
        try {
          const posts = await getPosts(batch);
          if (Array.isArray(posts)) allPosts.push(...posts);
        } catch (err) {
          console.error('Failed to fetch posts batch', err);
        }
      }

      if (allPosts.length === 0) return;

      setPostContext(prev => {
        const newContext = { ...prev };
        allPosts.forEach((p: any) => {
          if (p && p.uri) newContext[p.uri] = p;
        });
        const keys = getCacheKeys();
        AsyncStorage.setItem(keys.context, JSON.stringify(newContext));
        return newContext;
      });
    } catch (e) {
      console.error('Failed to fetch post context', e);
    }
  };

  const fetchNotifications = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await listNotifications(isRefresh ? undefined : cursor);
      
      setNotifications(prev => {
        let updated;
        if (isRefresh) {
          updated = res.notifications;
        } else {
          const existingKeys = new Set(prev.map(n => n.cid + n.indexedAt));
          const newItems = [];
          for (const n of res.notifications) {
            const key = n.cid + n.indexedAt;
            if (!existingKeys.has(key)) {
              newItems.push(n);
              existingKeys.add(key);
            }
          }
          updated = [...prev, ...newItems];
        }
        const keys = getCacheKeys();
        AsyncStorage.setItem(keys.notifs, JSON.stringify(updated));
        return updated;
      });

      setCursor(res.cursor);

      // Collect URIs for context
      const urisToFetch = res.notifications
        .map((n: any) => n.reasonSubject || (n.reason === 'reply' || n.reason === 'quote' ? n.uri : null))
        .filter((uri: string | null) => uri && !postContext[uri]);
      
      if (urisToFetch.length > 0) {
        await fetchPostContext(urisToFetch as string[]);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [cursor, postContext]);

  useEffect(() => {
    loadCache().then(() => {
      fetchNotifications(true);
    });
  }, []);

  const groupedData = useMemo(() => {
    const groups: GroupedNotification[] = [];
    
    notifications.forEach((notif) => {
      const key = notif.reasonSubject 
        ? `${notif.reason}-${notif.reasonSubject}`
        : notif.reason === 'follow' 
          ? `follow-${notif.indexedAt.split('T')[0]}` // Group follows by day
          : `${notif.reason}-${notif.uri}`;

      let group = groups.find(g => g.id === key);
      if (!group) {
        group = {
          id: key,
          reason: notif.reason,
          subjectUri: notif.reasonSubject,
          authors: [],
          items: [],
          indexedAt: notif.indexedAt,
        };
        groups.push(group);
      }
      
      if (!group.authors.find(a => a.did === notif.author.did)) {
        group.authors.push(notif.author);
      }
      group.items.push(notif);
      
      // Keep the latest indexedAt
      if (new Date(notif.indexedAt) > new Date(group.indexedAt)) {
        group.indexedAt = notif.indexedAt;
      }
    });

    return groups.sort((a, b) => new Date(b.indexedAt).getTime() - new Date(a.indexedAt).getTime());
  }, [notifications]);

  const getNotifIcon = (reason: string) => {
    switch (reason) {
      case 'like': return { name: 'heart', color: '#FF4B4B' };
      case 'repost': return { name: 'repeat', color: '#00B900' };
      case 'follow': return { name: 'person-add', color: '#1DA1F2' };
      case 'mention': return { name: 'at', color: '#7E57C2' };
      case 'reply': return { name: 'chatbubble', color: '#1DA1F2' };
      case 'quote': return { name: 'quote-outline', color: '#1DA1F2' };
      default: return { name: 'notifications', color: '#999' };
    }
  };

  const getNotifText = (group: GroupedNotification) => {
    const count = group.authors.length;
    const firstAuthor = group.authors[0].displayName || group.authors[0].handle;
    const others = count > 1 ? ` ${t('notifications.otherCount', { count: count - 1 })}` : '';
    
    switch (group.reason) {
      case 'like': return t('notifications.like', { author: firstAuthor, others });
      case 'repost': return t('notifications.repost', { author: firstAuthor, others });
      case 'follow': return t('notifications.follow', { author: firstAuthor, others });
      case 'mention': return t('notifications.mention', { author: firstAuthor });
      case 'reply': return t('notifications.reply', { author: firstAuthor });
      case 'quote': return t('notifications.quote', { author: firstAuthor });
      default: return t('notifications.default', { author: firstAuthor });
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}分前`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}時間前`;
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const renderItem = ({ item }: { item: GroupedNotification }) => {
    const icon = getNotifIcon(item.reason);
    const contextUri = item.subjectUri || (item.reason === 'reply' || item.reason === 'quote' ? item.items[0].uri : null);
    const contextPost = contextUri ? postContext[contextUri] : null;

    return (
      <TouchableOpacity 
        style={styles.notifItem}
        onPress={() => {
          if (contextPost) {
            onSelectPost?.(contextPost);
          }
        }}
      >
        <View style={styles.leftSection}>
          <View style={styles.avatarStack}>
            {item.authors.slice(0, 3).map((author, i) => (
              <TouchableOpacity key={author.did} onPress={() => onSelectUser?.(author.did)}>
                <Image 
                  source={{ uri: author.avatar }} 
                  style={[styles.avatar, { marginLeft: i > 0 ? -20 : 0, zIndex: 10 - i }]} 
                />
              </TouchableOpacity>
            ))}
          </View>
          <View style={[styles.notifIconBadge, { backgroundColor: icon.color }]}>
            <Ionicons name={icon.name as any} size={10} color="white" />
          </View>
        </View>
        
        <View style={styles.notifInfo}>
          <Text style={styles.notifText}>
            {getNotifText(item)}
          </Text>
          
          {contextPost && (
            <View style={styles.contextBox}>
              <Hyperlink 
                linkDefault={true} 
                linkStyle={{ color: theme.colors.primary, textDecorationLine: 'underline' }} 
                style={styles.contextText}
                onHashtagPress={onSearchHashtag}
              >
                {extractPlainText(contextPost)}
              </Hyperlink>
            </View>
          )}
          
          <Text style={styles.notifTime}>{formatTime(item.indexedAt)}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('tabs.notifications')}</Text>
      </View>

      {loading && notifications.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : (
        <FlatList
          data={groupedData}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={() => fetchNotifications(true)} tintColor={theme.colors.primary} />
          }
          onEndReached={() => fetchNotifications()}
          onEndReachedThreshold={0.5}
          contentContainerStyle={styles.listContent}
        />
      )}
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    height: 60,
    backgroundColor: theme.colors.card,
    justifyContent: 'center',
    paddingHorizontal: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    color: theme.colors.text,
    fontSize: 20,
    fontWeight: 'bold',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  listContent: {
    paddingBottom: 20,
  },
  notifItem: {
    flexDirection: 'row',
    padding: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  leftSection: {
    position: 'relative',
    marginRight: 12,
    width: 60,
    alignItems: 'center',
  },
  avatarStack: {
    flexDirection: 'row',
    height: 48,
    alignItems: 'center',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.cardSecondary,
    borderWidth: 2,
    borderColor: theme.colors.card,
  },
  notifIconBadge: {
    position: 'absolute',
    bottom: 0,
    right: 4,
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: theme.colors.card,
    zIndex: 20,
  },
  notifInfo: {
    flex: 1,
  },
  notifText: {
    fontSize: 14,
    color: theme.colors.text,
    lineHeight: 20,
    fontWeight: '500',
  },
  contextBox: {
    backgroundColor: theme.colors.cardSecondary,
    padding: 8,
    borderRadius: 4,
    marginTop: 8,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.border,
  },
  contextText: {
    fontSize: 12,
    color: theme.colors.text,
  },
  notifTime: {
    fontSize: 11,
    color: theme.colors.textMuted,
    marginTop: 6,
  },
});

export default Notifications;
